import { AfterViewInit, ChangeDetectorRef, Component, EventEmitter, Inject, Input, OnInit, Output, ViewContainerRef, ViewEncapsulation } from '@angular/core';
import { User } from 'pdauth';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

import { environment } from '../../../environment';
import { IAuthenticationService } from '../../../services/interfaces/IAuthenticationService';
import { ILoggingService } from '../../../services/interfaces/ILoggingService';
import { LayerService } from '../../../services/layer.service';
import { MapService } from '../../../services/map.service';
import { UserService } from '../../../services/user.service';
import { IMapCoreMap } from '../../../types';
import { BaseComponent } from '../../base.component';

/* tslint:disable:template-cyclomatic-complexity */
@Component({
    encapsulation: ViewEncapsulation.ShadowDom,
    selector: 'mc-share-map',
    templateUrl: './share-map.component.html',
    styleUrls: ['../../sharedStyles/base.scss', './share-map.component.scss']
})
export class ShareMapComponent extends BaseComponent implements OnInit, AfterViewInit {

    @Input() appName: string;

    private _map: IMapCoreMap[];
    @Input()
    get map() {
        return this._map;
    }
    set map(value: IMapCoreMap[]) {
        if (!Array.isArray(value)) {
            value = value ? [value] : null;
        }
        this._map = value;
        this.setShareMapObject();
    }

    get mustSave(): boolean {
        return this.requiresSave() && !this.requiresCopy();
    }

    get mustSaveUnsaved(): boolean {
        return this.hasUnsaved() && !this.requiresCopy();
    }

    get requireSaveCopy(): boolean {
        return this.requiresCopy();
    }

    get disableForm(): boolean {
        return this.formDisabled();
    }

    get showAddButton(): boolean {
        return this.showFreeFormButton();
    }

    get noRecipient(): boolean {
        return !this.hasRecipients();
    }
    outputSearchText = new Subject<string>();

    canViewUsers = false;
    canViewCWOPAData = false;
    mapUrls: string[];
    shareObj: IShareObject[];
    user: User;
    searchPlaceholder = 'Email or Name';
    inputSearchText: string;
    shareCandidates: User[];
    shareCandidatesError: string;
    unownedMaps: IShareObject[];
    unshareableMaps: { name: string; layers: string[] }[];
    loading: { status: boolean; text: string };

    @Output() readonly closeShareMap: EventEmitter<void> = new EventEmitter<void>();
    @Output() readonly openSaveMap: EventEmitter<void> = new EventEmitter<void>();

    @Input()
    email = {
        recipients: [],
        message: '',
        address: ''
    };

    loadMessage;
    shared = false;
    internalEmailOnly = true;
    includeADGroups = false;
    allowShareWithSelf = true;
    warn: any;
    success = false;
    error: any;
    info: any;
    loadingEmails = false;
    @Input() notifyUserByEmail = true;

    constructor(_vcr: ViewContainerRef,
                private readonly layerService: LayerService,
                private readonly mapService: MapService,
                private readonly userService: UserService,
                private readonly changeRef: ChangeDetectorRef,
                @Inject('authenticationService') public authService: IAuthenticationService,
                @Inject('LoggingService') public loggingService: ILoggingService) {
        super(_vcr);
        this.registerEvent('closeShareMap', this.closeShareMap);
        this.registerEvent('openSaveMap', this.openSaveMap);

        const milliseconds = 500;
        this.outputSearchText
            .pipe(debounceTime(milliseconds))
            .subscribe(async searchText => {
                this.shareCandidates = [];
                if (searchText.length !== 0) {
                    // we can add valid CWOPA users to PDAuth automatically if needed.
                    if (this.canViewUsers && searchText.endsWith('@pa.gov')) {
                        await this.userService.registerUser(searchText);
                    }
                    this.getUsers(searchText);
                } else {
                    this.shareCandidatesError = null;
                }
            });

        authService.permissions.subscribe(permissions => {
            this.canViewUsers = permissions && permissions.includes('View Users');
            this.canViewCWOPAData = permissions && permissions.includes('View CWOPA Data');
        });
    }

    ngOnInit() {
        this.authService.GetCurrentUser().subscribe(user => {
            this.user = user;
        });
        this.loading = { status: false, text: 'Loading...' };
    }

    ngAfterViewInit() {
        this.isReady();
    }

    async setShareMapObject() {
        this.resetAlerts();
        if (this.map) {
            this.shareObj = [];

            this.shareObj = await Promise.all(this.map.map(async (map) => {
                const mapData = await this.mapService.retrieveMap(map, this.user.id);
                return {
                    label: 'Map',
                    id: mapData.id,
                    data: mapData,
                    title: mapData.name
                };
            }));
            this.loadMapUrls();
            this.changeRef.detectChanges();
        }
    }

    loadMapUrls() {
        this.mapUrls = [];
        for (const s of this.shareObj) {
            this.mapUrls.push(this.mapService.getMapUrl(s.id));
        }
    }

    hasRecipients() {
        return this.email.recipients.length > 0;
    }

    requiresSave() {
        let retval = false;

        for (const s of this.shareObj) {
            if (!(!!s.id) || s.data.accessLevel !== 'OWNER') {
                retval = true;
            }
        }

        return retval;
    }

    requiresCopy() {
        let retval = false;
        this.unownedMaps = [];
        for (const s of this.shareObj) {
            if (s.data.accessLevel === 'VIEWER') {
                retval = true;
                this.unownedMaps.push(s);
            }
        }

        return retval;
    }

    async selectShareMap() {
        if (this.shareObj.length > 1) {
            await this.multiShare();
        } else {
            await this.share();
        }
    }

    async share() {
        if (!this.email.recipients || !this.email.recipients.length) { return; }

        this.loading = { text: 'Sharing map...', status: true };

        if (await this.hasUnshareableLayers()) {
            this.loading.status = false;
            return;
        }

        const data = this.shareObj.map(s => ({ title: s.title, message: this.email.message }));
        const recipients = this.constructRecipients(this.email.recipients);
        const sharedUserIds: number[][] = this.map.map(u => (u.sharedWith || []).map(s => s.userId));

        const errorArr = [];
        await Promise.all(this.shareObj.map(async (s) => {
            const sent = await this.mapService.shareMap(s.id, { data, recipients }, this.notifyUserByEmail, sharedUserIds);
            if (sent) {
                this.updateSharedUsers(recipients.map(r => r.id));
            } else {
                errorArr.push(`We are unable to share your ${s.label.toLowerCase()} ${s.title.toLowerCase()} at this time.`);
            }
        }));
        if (errorArr.length === 1) {
            this.error = `We are unable to share your ${errorArr[0].label.toLowerCase()} ${errorArr[0].title.toLowerCase()} at this time.`;
        } else if (errorArr.length > 1) {
            this.error = 'We are unable to share the following maps:';
            for (const err of errorArr) {
                this.error += ` ${err.label.toLowerCase()}${err.title.toLowerCase()}`;
            }
        } else {
            this.success = true;
            this.shared = true;
        }
        this.loading.status = false;
        this.resetForm();
    }

    async multiShare() {
        if (!this.email.recipients || !this.email.recipients.length) { return; }

        this.loading = { text: 'Sharing map...', status: true };

        if (await this.hasUnshareableLayers()) {
            this.loading.status = false;
            return;
        }

        const data = this.shareObj.map(s => ({ title: s.title, message: this.email.message }));
        const recipients = this.constructRecipients(this.email.recipients);
        const sharedUserIds: number[][] = this.map.map(u => (u.sharedWith || []).map(s => s.userId));

        const errorArr = [];
        const allSharedMapIds = [];
        this.shareObj.map(s => { allSharedMapIds.push(s.id); });
        const sent = await this.mapService.shareMaps(allSharedMapIds, { data, recipients }, this.notifyUserByEmail, sharedUserIds);
        if (sent) {
            this.updateSharedUsers(recipients.map(r => r.id));
        } else {
            allSharedMapIds.forEach(map => {
                errorArr.push(`We are unable to share your ${map.label.toLowerCase()} ${map.title.toLowerCase()} at this time.`);
            });
        }
        if (errorArr.length === 1) {
            this.error = `We are unable to share your ${errorArr[0].label.toLowerCase()} ${errorArr[0].title.toLowerCase()} at this time.`;
        } else if (errorArr.length > 1) {
            this.error = 'We are unable to share the following maps:';
            for (const err of errorArr) {
                this.error += ` ${err.label.toLowerCase()}${err.title.toLowerCase()}`;
            }
        } else {
            this.success = true;
            this.shared = true;
        }
        this.loading.status = false;
        this.resetForm();
    }

    PUBLIC_LAYER_NAMES = new Set([
        'View Public Data',
        'MapCore Public Layers',
        'Mapcore Public Layers'
    ]);

    private updateSharedUsers(recipientUserIds: number[]) {
        for (const map of this.map) {
            if (!map.sharedWith) {
                map.sharedWith = [];
            }

            for (const userId of recipientUserIds) {
                if (!map.sharedWith.some(s => s.userId === userId)) {
                    map.sharedWith.push({ userId });
                }
            }
        }
    }

    async hasUnshareableLayers(): Promise<boolean> {
        this.unshareableMaps = null;
        const mapIds = this.shareObj.map(m => m.id);
        const maps = await this.mapService.getMaps(mapIds, 'name, isPublic, layers { id, name }');
        let unshareableMaps = maps.map(m => ({ name: m.name, layers: [] }));
        const layerMaps = {};
        const layerIds = [];

        for (const map of maps) {
            if (!map.isPublic) {
                // No check needed, we can share a map with non-public layers as long as the map is non-public
                continue;
            }

            for (const layer of map.layers) {
                layerMaps[layer.id] = unshareableMaps.filter(um => um.name === map.name)[0];
                layerIds.push(layer.id);
            }
        }

        if (layerIds.length > 0) {
            const layers = await this.layerService.getLayersFromApi(layerIds, 'id, parentId, name, permissions { name }, sources { sourceType }');
            const parentLayerIds = layers.map(l => l.parentId);
            const parentLayers = await this.layerService.getLayersFromApi(parentLayerIds, 'id, name, permissions { name }');

            for (const layer of layers) {
                let permissions = layer.permissions;

                if (layer.sources.every(s => s.sourceType === 'FEATURE_ENDPOINT')) {
                    continue;
                }

                if (layer.parentId) {
                    const parentLayer = parentLayers.filter(pl => pl.id === layer.parentId)[0];
                    permissions = permissions.concat(parentLayer.permissions);
                }

                if (!permissions.some(p => this.PUBLIC_LAYER_NAMES.has(p.name))) {
                    layerMaps[layer.id].layers.push(layer.name);
                }
            }
        }

        unshareableMaps = unshareableMaps.filter(um => um.layers.length > 0);
        unshareableMaps.sort((a, b) => a.name.localeCompare(b.name));

        if (unshareableMaps.length > 0) {
            this.unshareableMaps = unshareableMaps;
            return true;
        } else {
            return false;
        }
    }

    mapRecipient(recipient) {
        return { email: recipient.email, id: recipient.id || null };
    }

    constructRecipients(recipients) {
        const retVal: { email: string; id: number }[] = [];
        for (const recipient of recipients) {
            if (!recipient.groupMembers) {
                this.addRecipientIfNotPresent(this.mapRecipient(recipient), retVal);
            } else {
                recipient.groupMembers.forEach(x => this.addRecipientIfNotPresent(this.mapRecipient(x), retVal));
            }
        }
        return retVal;
    }

    addRecipientIfNotPresent(recipient, emailList) {
        if (emailList.filter(x => x.email === recipient.email).length === 0) {
            emailList.push(this.mapRecipient(recipient));
        }
    }

    async addFreeFormRecipient(emailAddress) {
        this.resetAlerts();
        //for now, allow sharing maps with yourself
        if (this.allowShareWithSelf || emailAddress !== this.user.emailAddress) {
            this.loadMessage = 'Verifying recipients';
            const recipient = await this.userService.verifyAccount(emailAddress);
            if (recipient) {

                await Promise.all(this.shareObj.map(async (s) => {
                    const users = await this.mapService.getMapUsers(s.id);
                    if (users.indexOf(recipient.email) === -1) {
                        const { email, firstName, lastName, id } = recipient;
                        this.addRecipient({ email, firstName, lastName, id });
                    } else {
                        this.info = {
                            recipient: recipient.email
                        };
                        this.email.address = '';
                    }
                }));

            } else {
                this.error = `No user found for email address ${emailAddress}`;
                this.email.address = '';
            }
            this.loadMessage = undefined;

        } else {
            this.error = 'You may not share a map with yourself';
            this.email.address = '';
        }
    }

    showFreeFormButton() {
        return (/\S+@\S+\.\S+/i).test(this.inputSearchText || '');
    }

    resetError() {
        this.error = undefined;
        this.unshareableMaps = null;
    }

    resetWarning() {
        this.warn = undefined;
    }

    resetSuccess() {
        this.success = undefined;
    }

    resetInfo() {
        this.info = undefined;
    }

    resetAlerts() {
        this.loadingEmails = false;
        this.resetError();
        this.resetWarning();
        this.resetSuccess();
        this.resetInfo();
    }

    resetForm() {
        this.email.address = '';
        this.email.message = '';
        this.email.recipients.length = 0;
    }

    async addRecipient(recipient) {
        this.resetAlerts();

        await Promise.all(this.shareObj.map(async (s) => {
            const existingUsers: any[] = await this.mapService.getMapUsers(s.id);
            if (!existingUsers.some(val => val.id === recipient.id)) {
                const recipients = this.email.recipients;
                const emails = recipients.map((r) => {
                    return r.email;
                });

                if (emails.indexOf(recipient.email) < 0) {
                    recipients.push(recipient);
                }
                this.email.address = '';
            } else {
                this.info = {
                    recipient: recipient.email
                };
                this.email.address = null;
            }
        }));
    }

    removeRecipient(recipient) {
        const recipients = this.email.recipients,
            index = recipients.indexOf(recipient);

        if (index >= 0) {
            recipients.splice(index, 1);
        }
    }

    addMember(group: any[], member) {
        this.addRecipient(member);
        group.splice(group.indexOf(member), 1);
    }

    removeMember(group: any[], member) {
        group.splice(group.indexOf(member), 1);
    }

    async getUsers(searchText) {
        this.loadingEmails = true;

        await Promise.all(this.shareObj.map(async () => {
            const maxRows = 200;
            try {
                const results = await this.userService.searchUsers(searchText, maxRows, this.includeADGroups, !this.canViewCWOPAData);
                this.shareCandidates = results;
            } catch (er) {
                this.shareCandidatesError = 'An error occured';
                this.loggingService.Log('Error retrieving users', er);
            }
        }));

        this.loadingEmails = false;
    }

    selectUser(user: User) {
        this.email.recipients.push(user);
        this.inputSearchText = '';
        this.shareCandidates = [];
        this.shareCandidatesError = null;
    }

    formatUserSearchResult(result) {
        if (result) {
            if (typeof result === 'string') {
                return result;
            } else {
                if (result.id < 0) {
                    const count = result.groupMembers.length;
                    return `${result.firstName} ( ${count}  Members)`;
                } else {
                    return `${result.firstName}  ${result.lastName} ( ${result.email} )`;
                }
            }
        }
        return '[Error when formatting result]';
    }

    hasRecipientsError() {
        return !this.email.recipients.length;
    }

    hasFormError() {
        return this.requiresSave() || this.hasRecipients();
    }

    hasUnsaved() {
        // return !this.requiresSave();
        return false;
    }

    async saveMaps() {

        if (this.mustSave) {
            //for single map that needs saved, close the window and open the saveMaps component
            this.close();
            this.openSaveMap.emit();
        } else {
            //for multiple maps:
            // multi-share is only accessible in map-catalog -> all maps in map-catalog are already saved
            // The only 'saving' that needs done would be making unowned map access level set to owned

            if (this.unownedMaps.length > 0) {

                this.loading = { status: true, text: 'Creating Copies...' };

                const mapIds = this.unownedMaps.map(x => x.id);
                const appId: number = environment.appId;
                //make copies of un-owned maps, and become the owner
                const mapsCopied: IMapCoreMap[] = await this.mapService.copyMaps(mapIds, appId);

                this.map = mapsCopied;
                this.loading.status = false;
            }

        }
    }

    async close() {
        this.inputSearchText = '';
        this.closeShareMap.emit();
    }

    formDisabled() {
        return this.requiresCopy() || this.requiresSave();
    }

}

export interface IShareObject {
    label: string;
    id: any;
    data: any;
    title: string;
}

export const EmailRegex = new RegExp(/(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])/gi);
